// Client-side preview builder for the Manufacturing → Models bulk-import
// modal. Parses raw text (JSON or CSV), groups rows into recipes, and
// partitions each recipe's lines into matched vs pending / unresolved.
//
// mode "create": colliding codes → conflict code_exists (skipped).
// mode "upsert": colliding codes → conflict will_replace (BOM replace).

import { parseBulkImport } from "./bulkImportParser";
import type { MfgMaterial, MfgMaterialUse, MfgRecipeSummary } from "./types";
import type {
  ImportRecipe,
  ImportRecipeConflict,
  ImportRecipeLine,
  ImportSkippedRow,
  RecipeBulkImportPreview,
} from "./recipeBulkImportPreviewTypes";
import {
  buildLine,
  buildMaterialIndex,
  buildMaterialUseCodeIndex,
  buildRecipeCodeIndex,
  buildRecipeCodeSet,
  lineIsMatched,
  lineIsPendingMaterial,
  lineIsUnresolvedRef,
  parseKind,
  parseTags,
  trackIgnoredKeys,
  type BuildLineIndexes,
  type RawRow,
} from "./recipeBulkImportPreviewHelpers";

export type {
  ImportRecipe,
  ImportRecipeLine,
  ImportSkippedRow,
  RecipeBulkImportPreview,
};

export const RECIPE_REQUIRED_KEYS = ["recipe_code", "recipe_name"] as const;

const RECOGNIZED_RECIPE_KEYS = new Set<string>([
  ...RECIPE_REQUIRED_KEYS,
  "recipe_kind",
  "recipe_tags",
  "recipe_notes",
  "lines",
]);
const RECOGNIZED_LINE_KEYS = new Set<string>([
  "line_kind",
  "line_material_name",
  "line_component_recipe_code",
  "line_material_use_code",
  "line_quantity",
  "line_role",
  "line_slot_label",
  "line_upgrade_price_delta",
  "line_swappable",
]);
const RECOGNIZED_NESTED_LINE_KEYS = new Set<string>([
  "line_kind",
  "kind",
  "material_name",
  "component_recipe_code",
  "material_use_code",
  "quantity",
  "role",
  "slot_label",
  "upgrade_price_delta",
  "swappable",
]);

function conflictForCode(
  code: string,
  existingCodes: Set<string>,
  mode: "create" | "upsert",
): ImportRecipeConflict {
  if (!existingCodes.has(code)) return null;
  return mode === "upsert" ? "will_replace" : "code_exists";
}

function recipeFlags(lines: ImportRecipeLine[]) {
  return {
    hasPending: lines.some(lineIsPendingMaterial),
    hasUnresolvedRefs: lines.some(lineIsUnresolvedRef),
  };
}

function summarizeTotals(recipes: ImportRecipe[]) {
  let linesTotal = 0;
  let linesMatched = 0;
  let linesPending = 0;
  let linesUnresolved = 0;
  let recipesReadyToCreate = 0;
  let recipesWillReplace = 0;
  let recipesWithPending = 0;
  let recipesWithUnresolvedRefs = 0;
  let recipesConflicting = 0;
  for (const r of recipes) {
    linesTotal += r.lines.length;
    for (const l of r.lines) {
      if (lineIsMatched(l)) linesMatched += 1;
      else if (lineIsPendingMaterial(l)) linesPending += 1;
      else if (lineIsUnresolvedRef(l)) linesUnresolved += 1;
    }
    if (r.conflict === "code_exists") recipesConflicting += 1;
    else if (r.hasUnresolvedRefs) recipesWithUnresolvedRefs += 1;
    else if (r.conflict === "will_replace") {
      recipesWillReplace += 1;
      if (r.hasPending) recipesWithPending += 1;
    } else if (r.hasPending) recipesWithPending += 1;
    else recipesReadyToCreate += 1;
  }
  return {
    recipes: recipes.length,
    recipesReadyToCreate,
    recipesWillReplace,
    recipesWithPending,
    recipesWithUnresolvedRefs,
    recipesConflicting,
    linesTotal,
    linesMatched,
    linesPending,
    linesUnresolved,
  };
}

type BuildCtx = {
  mode: "create" | "upsert";
  indexes: BuildLineIndexes;
  existingCodes: Set<string>;
  recipesByCode: Map<string, ImportRecipe>;
  skipped: ImportSkippedRow[];
  ignoredKeys: Set<string>;
};

function buildJsonRecipes(rows: RawRow[], ctx: BuildCtx) {
  rows.forEach((raw, recipeIdx) => {
    trackIgnoredKeys(raw, RECOGNIZED_RECIPE_KEYS, ctx.ignoredKeys);

    const code = typeof raw.recipe_code === "string" ? raw.recipe_code.trim() : "";
    const name = typeof raw.recipe_name === "string" ? raw.recipe_name.trim() : "";
    const headerMissing: string[] = [];
    if (!code) headerMissing.push("recipe_code");
    if (!name) headerMissing.push("recipe_name");
    if (headerMissing.length > 0) {
      ctx.skipped.push({ idx: recipeIdx, missing: headerMissing, context: "recipe-header" });
      return;
    }
    if (ctx.recipesByCode.has(code)) {
      ctx.skipped.push({
        idx: recipeIdx,
        missing: ["duplicate_recipe_code_in_input"],
        context: "recipe-header",
      });
      return;
    }

    const lines: ImportRecipeLine[] = [];
    const rawLines = Array.isArray(raw.lines) ? (raw.lines as RawRow[]) : [];
    rawLines.forEach((rawLine, lineIdx) => {
      if (typeof rawLine !== "object" || rawLine === null) return;
      trackIgnoredKeys(rawLine, RECOGNIZED_NESTED_LINE_KEYS, ctx.ignoredKeys);
      const built = buildLine(rawLine, lineIdx, ctx.indexes, "json");
      if (!built) return;
      if ("skipped" in built) {
        ctx.skipped.push({ idx: lineIdx, missing: built.skipped.missing, context: "line" });
        return;
      }
      lines.push(built.line);
    });

    const flags = recipeFlags(lines);
    ctx.recipesByCode.set(code, {
      code,
      name,
      kind: parseKind(raw.recipe_kind),
      tags: parseTags(raw.recipe_tags),
      notes: typeof raw.recipe_notes === "string" ? raw.recipe_notes : "",
      lines,
      hasPending: flags.hasPending,
      hasUnresolvedRefs: flags.hasUnresolvedRefs,
      conflict: conflictForCode(code, ctx.existingCodes, ctx.mode),
    });
  });
}

function buildCsvRecipes(rows: RawRow[], ctx: BuildCtx) {
  const recognizedFlatKeys = new Set<string>([
    ...RECOGNIZED_RECIPE_KEYS,
    ...RECOGNIZED_LINE_KEYS,
  ]);
  rows.forEach((raw, rowIdx) => {
    trackIgnoredKeys(raw, recognizedFlatKeys, ctx.ignoredKeys);
    const code = typeof raw.recipe_code === "string" ? raw.recipe_code.trim() : "";
    if (!code) {
      ctx.skipped.push({ idx: rowIdx, missing: ["recipe_code"], context: "recipe-header" });
      return;
    }
    let recipe = ctx.recipesByCode.get(code);
    if (!recipe) {
      const name = typeof raw.recipe_name === "string" ? raw.recipe_name.trim() : "";
      if (!name) {
        ctx.skipped.push({ idx: rowIdx, missing: ["recipe_name"], context: "recipe-header" });
        return;
      }
      recipe = {
        code,
        name,
        kind: parseKind(raw.recipe_kind),
        tags: parseTags(raw.recipe_tags),
        notes: typeof raw.recipe_notes === "string" ? raw.recipe_notes : "",
        lines: [],
        hasPending: false,
        hasUnresolvedRefs: false,
        conflict: conflictForCode(code, ctx.existingCodes, ctx.mode),
      };
      ctx.recipesByCode.set(code, recipe);
    }
    const built = buildLine(raw, rowIdx, ctx.indexes, "csv");
    if (!built) return;
    if ("skipped" in built) {
      ctx.skipped.push({ idx: rowIdx, missing: built.skipped.missing, context: "line" });
      return;
    }
    recipe.lines.push(built.line);
    const flags = recipeFlags(recipe.lines);
    recipe.hasPending = flags.hasPending;
    recipe.hasUnresolvedRefs = flags.hasUnresolvedRefs;
  });
}

export function buildRecipeBulkImportPreview(
  rawText: string,
  existingMaterials: ReadonlyArray<MfgMaterial>,
  existingRecipes: ReadonlyArray<MfgRecipeSummary>,
  mode: "create" | "upsert" = "create",
  existingMaterialUses: ReadonlyArray<MfgMaterialUse> = [],
): RecipeBulkImportPreview {
  if (!rawText.trim()) return { kind: "empty" };
  const parsed = parseBulkImport(rawText);
  if (!parsed.ok) return { kind: "parse-error", message: parsed.error };
  if (parsed.rows.length === 0) {
    return { kind: "parse-error", message: "No rows found in input." };
  }

  const firstKeys = new Set(Object.keys(parsed.rows[0]));
  const firstMissing = RECIPE_REQUIRED_KEYS.filter((k) => !firstKeys.has(k));
  if (firstMissing.length > 0) {
    return { kind: "first-row-error", missing: firstMissing, format: parsed.format };
  }

  const ctx: BuildCtx = {
    mode,
    indexes: {
      materialIndex: buildMaterialIndex(existingMaterials),
      recipeCodeIndex: buildRecipeCodeIndex(existingRecipes),
      useCodeIndex: buildMaterialUseCodeIndex(existingMaterialUses),
    },
    existingCodes: buildRecipeCodeSet(existingRecipes),
    recipesByCode: new Map<string, ImportRecipe>(),
    skipped: [],
    ignoredKeys: new Set<string>(),
  };

  if (parsed.format === "json") {
    buildJsonRecipes(parsed.rows, ctx);
  } else {
    buildCsvRecipes(parsed.rows, ctx);
  }

  const recipes = Array.from(ctx.recipesByCode.values());
  return {
    kind: "ok",
    format: parsed.format,
    mode,
    recipes,
    skipped: ctx.skipped,
    ignoredKeys: Array.from(ctx.ignoredKeys).sort(),
    totals: summarizeTotals(recipes),
  };
}
